import { useCallback, useMemo, useRef } from 'react'
import { GridColDef, GridRowParams } from '@mui/x-data-grid'
import { ROW_MENU_COLUMN } from 'shared/constants/dataGrid'
import SearchHeader from '../../SearchHeader'
import RowActionsMenu from '../../RowActionsMenu'
import { SingleSelectSearchController } from '../../SingleSelectTable'
import { AdminRow } from '../types'

interface UseAdminsTableColumnsArgs {
  search: SingleSelectSearchController
  onRemoveAdmin: (userId: string) => void
  isRemoveAdminLoading: boolean
  emailLabel: string
  removeLabel: string
}

export const useAdminsTableColumns = ({
  search,
  onRemoveAdmin,
  isRemoveAdminLoading,
  emailLabel,
  removeLabel,
}: UseAdminsTableColumnsArgs) => {
  const searchValueRef = useRef(search.value)
  const searchActiveRef = useRef(search.active)
  const searchInputRef = useRef<HTMLInputElement>(null)

  searchValueRef.current = search.value
  searchActiveRef.current = search.active

  const handleToggleSearch = useCallback(
    (action?: 'toggle' | 'off') => {
      search.toggle(action, () => {
        window.setTimeout(() => {
          searchInputRef.current?.focus()
        }, 100)
      })
    },
    [search]
  )

  return useMemo<GridColDef<AdminRow>[]>(
    () => [
      {
        field: 'email',
        headerName: emailLabel,
        flex: 1,
        minWidth: 250,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderHeader: () => (
          <SearchHeader
            active={searchActiveRef.current}
            label={emailLabel}
            value={searchValueRef.current}
            inputRef={searchInputRef}
            onToggle={handleToggleSearch}
            onChange={search.setValue}
            onClear={search.clear}
          />
        ),
        valueGetter: params => params.row.email || '-',
      },
      {
        ...ROW_MENU_COLUMN,
        getActions: ({ row }: GridRowParams<AdminRow>) =>
          row.viewerCanRemove
            ? [
                <RowActionsMenu
                  key={`admins-row-actions-${row.id}`}
                  removeAction={{
                    label: removeLabel,
                    disabled: isRemoveAdminLoading,
                    onClick: () => onRemoveAdmin(row.id),
                  }}
                />,
              ]
            : [],
      },
    ],
    [
      handleToggleSearch,
      isRemoveAdminLoading,
      emailLabel,
      removeLabel,
      onRemoveAdmin,
      search.clear,
      search.setValue,
    ]
  )
}
